import { BUILT_IN_ISSUE_STATUSES } from "@/domains/pm/types";
import { BUILT_IN_ANCHOR_START, BUILT_IN_SORT_STEP } from "./statusCatalogue";
import type { StatusDescriptor } from "./statusCatalogue";

/**
 * Where the Kanban's columns sit, and what a drag has to write.
 *
 * Built-ins and customs share one ordering axis (see `visibleStatuses`), but
 * only the customs are stored — a built-in's position is fixed by its index, so
 * In Progress → Resolved → Closed keeps its order whatever else moves. That
 * makes "put this custom column here" a question about the numbers around it,
 * which is arithmetic, so it lives here and is tested headlessly.
 *
 * Every drop RENUMBERS the project's customs rather than nudging one value.
 * Writing a single midpoint is fewer updates, but consecutive integers have no
 * midpoint, so a column dragged back and forth in the same gap eventually has
 * nowhere to land. Renumbering costs a handful of writes on a rare action and
 * cannot run out of room.
 */

/** Spacing between customs inside one gap, and past the last built-in. */
const CUSTOM_STEP = 10;

/**
 * The rightmost built-in anchor. Derived, not written down twice — the
 * catalogue owns where the anchors are.
 */
const LAST_BUILT_IN_POSITION = Math.max(
  ...BUILT_IN_ISSUE_STATUSES.map((_, i) => BUILT_IN_ANCHOR_START + i * BUILT_IN_SORT_STEP)
);

/** The order a drag produces: `movedKey` lifted out and dropped at `toIndex`. */
export function moveColumn(
  ordered: StatusDescriptor[],
  movedKey: string,
  toIndex: number
): StatusDescriptor[] {
  const from = ordered.findIndex((d) => d.key === movedKey);
  if (from === -1 || toIndex < 0 || toIndex >= ordered.length) return ordered;
  const next = [...ordered];
  const [moved] = next.splice(from, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

/**
 * The `sort_order` each custom needs for `desired` to be the order that comes
 * back out of `visibleStatuses`. Only the values that actually change are
 * returned, so a drop that moves one column writes one row.
 *
 * Built-ins are anchors at 0, 100, 200 …; customs are distributed evenly in the
 * open interval between the anchors they fall between. Customs before the first
 * anchor take negative values — that is what lets a custom column sit in front
 * of In Progress at all.
 */
export function assignCustomSortOrders(desired: StatusDescriptor[]): Map<string, number> {
  const changed = new Map<string, number>();
  let runStart = 0;
  while (runStart < desired.length) {
    if (!desired[runStart].custom) {
      runStart += 1;
      continue;
    }
    // A maximal run of customs, and the built-in anchors either side of it.
    let runEnd = runStart;
    while (runEnd < desired.length && desired[runEnd].custom) runEnd += 1;
    const before = runStart > 0 ? desired[runStart - 1].sortOrder : null;
    const after = runEnd < desired.length ? desired[runEnd].sortOrder : null;
    const count = runEnd - runStart;
    const run = desired.slice(runStart, runEnd);

    // Leave a run whose stored values already produce this order. Renumbering
    // it anyway would write every custom status in the project on a drop that
    // moved one — and would drag legacy rows off their original numbers for no
    // reason at all.
    if (alreadyOrdered(run, before, after)) {
      runStart = runEnd;
      continue;
    }

    for (let i = 0; i < count; i++) {
      const value = positionIn(before, after, i, count);
      const d = desired[runStart + i];
      if (d.sortOrder !== value) changed.set(d.key, value);
    }
    runStart = runEnd;
  }
  return changed;
}

/** Do these values already sit in order, strictly inside their anchors? */
function alreadyOrdered(
  run: StatusDescriptor[],
  before: number | null,
  after: number | null
): boolean {
  if (before != null && run[0].sortOrder <= before) return false;
  if (after != null && run[run.length - 1].sortOrder >= after) return false;
  for (let i = 1; i < run.length; i++) {
    if (run[i].sortOrder <= run[i - 1].sortOrder) return false;
  }
  return true;
}

/** The i-th of `count` slots strictly between two anchors (either may be open). */
function positionIn(before: number | null, after: number | null, i: number, count: number): number {
  if (before == null && after == null) return i * CUSTOM_STEP;
  // Before the first built-in: count backwards from it, so the run keeps its
  // order and every value stays below the anchor.
  if (before == null) return (after as number) - (count - i) * CUSTOM_STEP;
  if (after == null) return before + (i + 1) * CUSTOM_STEP;
  // Between two anchors: evenly spaced, never touching either end.
  const gap = (after - before) / (count + 1);
  return Math.round(before + gap * (i + 1));
}

/**
 * Where a newly created status goes: the end of the board.
 *
 * Past the last built-in even on a project with no customs yet. It lands at 0
 * or above, which is also where every row written before this ordering existed
 * sits — so a new status and a legacy one agree about meaning "at the end".
 */
export function nextSortOrder(ordered: StatusDescriptor[]): number {
  const highest = ordered.reduce((best, d) => Math.max(best, d.sortOrder), 0);
  return Math.max(highest, LAST_BUILT_IN_POSITION) + CUSTOM_STEP;
}
